import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import type z from 'zod';

import { RadioGroup } from '@nesso/ui/primitives/radio-group';

import {
    identifierStepSchema,
    type StepProps,
    useUpdateOnboardingStepOptions,
} from '@/entities/practice-member';
import {
    notesSettingsOptions,
    useUpdateNotesSettingsOptions,
} from '@/entities/practice-member-settings';
import { CardFormSection } from '@/shared/ui/forms/card-form-section';
import { RadioButtonItem } from '@/shared/ui/forms/radio-button-item';

export function OnboardingIdentifierStep(props: StepProps) {
    const { data: notesSettings } = useSuspenseQuery(notesSettingsOptions());

    const form = useForm<z.infer<typeof identifierStepSchema>>({
        mode: 'onChange',
        resolver: zodResolver(identifierStepSchema),
        defaultValues: { identifier: notesSettings.value.identifier },
    });
    const {
        formState: { isDirty, isSubmitting, isSubmitted, isValid, errors },
    } = form;
    const mutation = useMutation(useUpdateNotesSettingsOptions());
    const updateOnboardingStepMutation = useMutation(useUpdateOnboardingStepOptions());

    const onSubmit = async (data: z.infer<typeof identifierStepSchema>) => {
        try {
            if (props.nextStep) {
                updateOnboardingStepMutation.mutate(props.nextStep);
            }
            if (isDirty) {
                await mutation.mutateAsync(data);
            }
            props.onNext();
        } catch {
            console.error('Failed to update identifier step');
        }
    };

    const handleManualSubmit = () => {
        form.handleSubmit(onSubmit)();
    };

    const hasError = isSubmitted && !isValid;

    return (
        <FormProvider {...form}>
            <CardFormSection
                title="Choose the identifier used in your notes"
                description={hasError ? errors.identifier?.message : undefined}
                isError={hasError}
                isFatalError={mutation.isError}
                progressValue={props.progress}
                disabledBackButton={isSubmitting}
                disabledNextButton={isSubmitting}
                onNext={handleManualSubmit}
                onBack={props.onBack}
            >
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Controller
                        control={form.control}
                        name="identifier"
                        render={({ field }) => (
                            <RadioGroup
                                {...field}
                                onValueChange={field.onChange}
                                disabled={isSubmitting}
                                className="flex flex-col gap-6 w-full sm:max-w-[25rem]"
                            >
                                <RadioButtonItem value="CLIENT" title="Client" />
                                <RadioButtonItem value="PATIENT" title="Patient" />
                            </RadioGroup>
                        )}
                    />
                </form>
            </CardFormSection>
        </FormProvider>
    );
}
